import { Injectable } from "@angular/core";
import { MCCodeListWrapperService, MC_RESPONSE_STATUS_CODE, MCSelectItemNested } from "@maslul/common-services";
import { Subscription, Observable, ReplaySubject, take, map } from "rxjs";
import { RequestApprovalTypeDetailsStore } from "../store/approval-type-details-store";
import { SecondRequest } from "../../models/request-submission-methods/approvalType/secondRequest";
import { MIUnionRequestHttpService } from "../shared/union-request-http";

@Injectable({
    providedIn: 'root',
})
export class OpenRequestFromLinkService {
    subscriptions: Subscription[] = [];
    authorities: MCSelectItemNested[] = [];
    private authoritiesLoaded$ = new ReplaySubject<MCSelectItemNested[]>(1);

    constructor(private codeListService: MCCodeListWrapperService,
        private requestApprovalTypeDetailsStore: RequestApprovalTypeDetailsStore,
        private unionRequestHttpService: MIUnionRequestHttpService
    ) {
        this.initSubscriptions();
    }

    initSubscriptions() {
        this.subscriptions.push(
            this.codeListService.getAuthorityWithApproval(false).subscribe((authoritiesWithApproval: MCSelectItemNested[]) => {
                this.authorities = authoritiesWithApproval;
                this.authoritiesLoaded$.next(authoritiesWithApproval);
            })
        )
    }

    extractApprovalTypeDetailsFromKey(approvalTypeKey: string) {
        const factor = approvalTypeKey.toString().slice(0, 2);
        const code = approvalTypeKey.toString().slice(2, 4);
        const second = approvalTypeKey.toString().slice(4, 6);
        return { factor, code, second };
    }

    updateApprovalTypeDetailsByApTypeKeys(factor: string, code: string, second: string): Observable<boolean> {
        return this.authoritiesLoaded$.pipe(
            take(1),
            map((authorities) => {
                // 1. בדיקה שהרשות קיימת
                const selectedAuthority: MCSelectItemNested | undefined = authorities.find(auth => auth.value == factor);
                if (!selectedAuthority) {
                    return false;
                }

                const secondApprovalTypes: SecondRequest[] = this.getSecondApprovalTypes(selectedAuthority);

                // 2. בדיקה שה-typeCode קיים ברשימה
                const mainTypes = selectedAuthority?.items || [];
                const matchedMainType = mainTypes.find((mt: any) =>
                    parseInt(mt.additionalColumns?.['Code'] || mt.additionalCode || '0') == parseInt(code)
                );
                if (!matchedMainType) {
                    // רשות קיימת אבל typeCode לא - שומרים רק את הרשות
                    this.requestApprovalTypeDetailsStore.setAuthorityId(selectedAuthority.value?.toString() || "");
                    this.requestApprovalTypeDetailsStore.setApprovalTypes(secondApprovalTypes || []);
                    return false;
                }

                // 3. בדיקה שסוג האישור (second) קיים ברשימה
                const selectedApprovalType: SecondRequest | undefined = this.selectedApprovalType(secondApprovalTypes, code, second);
                if (!selectedApprovalType) {
                    // רשות ו-typeCode קיימים אבל second לא - שומרים רשות + typeCode
                    this.requestApprovalTypeDetailsStore.setAuthorityId(selectedAuthority.value?.toString() || "");
                    this.requestApprovalTypeDetailsStore.setTypeCode(matchedMainType.additionalColumns?.['Code'] || matchedMainType.additionalCode || code);
                    this.requestApprovalTypeDetailsStore.setApprovalMainTypeName(matchedMainType.label || "");
                    this.requestApprovalTypeDetailsStore.setApprovalTypes(secondApprovalTypes || []);
                    return false;
                }

                // 4. הכל תקין - שומרים את כל הנתונים
                this.setApprovalTypeDetails(selectedApprovalType);
                this.setAuthorityAndMainTypeDetails(selectedAuthority, code, secondApprovalTypes);
                this.updateApprovalTypeTabs();
                return true;
            })
        );
    }

    selectedApprovalType(secondApprovalTypes: SecondRequest[], typeCode: string, secondRequest: string): SecondRequest | undefined {
        return secondApprovalTypes.find(item => parseInt(item.typeCode) == parseInt(typeCode) && parseInt(item.secondRequest) == parseInt(secondRequest));
    }

    setAuthorityAndMainTypeDetails(selectedAuthority: MCSelectItemNested, typeCode: string, secondApprovalTypes: SecondRequest[]) {
        this.requestApprovalTypeDetailsStore.setAuthorityId(selectedAuthority?.value?.toString() || "");
        const mainTypes = selectedAuthority?.items || [];
        const matchedMainType = mainTypes.find((mt: any) =>
            parseInt(mt.additionalColumns?.['Code'] || mt.additionalCode || '0') == parseInt(typeCode)
        );
        this.requestApprovalTypeDetailsStore.setTypeCode(matchedMainType?.additionalColumns?.['Code'] || matchedMainType?.additionalCode || typeCode);
        this.requestApprovalTypeDetailsStore.setApprovalMainTypeName(matchedMainType?.label || mainTypes[0]?.label || "");
        this.requestApprovalTypeDetailsStore.setApprovalTypes(secondApprovalTypes || []);
    }


    setApprovalTypeDetails(approvalTypeDetails: SecondRequest) {
        const secondRequest: number = isNaN(parseInt(approvalTypeDetails.secondRequest))
            ? 0 : parseInt(approvalTypeDetails.secondRequest);
        this.requestApprovalTypeDetailsStore.setSecondRequest(secondRequest?.toString());

        const approvalTypeId: number = isNaN(parseInt(approvalTypeDetails.apTypeId))
            ? 0 : parseInt(approvalTypeDetails.apTypeId);
        this.requestApprovalTypeDetailsStore.setApprovalTypeId(approvalTypeId?.toString());
        this.requestApprovalTypeDetailsStore.setApprovalTypeName(approvalTypeDetails?.apTypeName);
        this.requestApprovalTypeDetailsStore.setIs4DigitsCustomItem(approvalTypeDetails.is4DigitsCustomItem);
    }

    getSecondApprovalTypes(selectedAuthority: MCSelectItemNested): SecondRequest[] {
        const mainTypes = selectedAuthority?.items || [];
        const allApprovalTypes: SecondRequest[] = [];
        mainTypes.forEach((mainType: any) => {
            const approvalTypes = mainType?.items || [];
            const mainTypeCode = mainType?.additionalColumns?.['Code'] || mainType?.additionalCode || '';
            approvalTypes.forEach((x: any) => {
                allApprovalTypes.push(
                    new SecondRequest(
                        x.value?.toString(),
                        x.label,
                        x.additionalColumns?.['MIApprovalTypeDescription'],
                        x.additionalColumns?.['Secondary'],
                        x.additionalColumns?.['CustomItem4Digits'] === 'True',
                        mainType?.label,
                        mainTypeCode,
                    )
                );
            });
        });
        return allApprovalTypes;
    }

    updateApprovalTypeTabs() {
        const approvalTypeId = this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId

        this.subscriptions.push(this.unionRequestHttpService.GetApprovalTabs([parseInt(approvalTypeId)]).subscribe((res) => {
            if (res.code === MC_RESPONSE_STATUS_CODE.SUCCESS) {
                this.requestApprovalTypeDetailsStore.setApprovalTabs(res.data);
            }

        }))
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}